import { create } from 'zustand';
import { customEmailTemplateService } from '../services/customEmailTemplateService';
import { definedLanguages } from '../types/customEmailTemplate';
import type { CustomEmailTemplateSetting } from '../types/customEmailTemplate';

interface CustomEmailTemplateState {
    template: CustomEmailTemplateSetting | null;
    loaded: boolean;
    /** Bumped on every save or delete, so drafts rendered from an older template are not reused. */
    version: number;
    /** Left uncaught: callers decide whether a failed load is worth surfacing. */
    fetch: () => Promise<CustomEmailTemplateSetting>;
    ensureLoaded: () => Promise<CustomEmailTemplateSetting | null>;
    save: (setting: CustomEmailTemplateSetting) => Promise<void>;
    remove: () => Promise<void>;
    reset: () => void;
}

export const useCustomEmailTemplateStore = create<CustomEmailTemplateState>((set, get) => ({
    template: null,
    loaded: false,
    version: 0,

    fetch: async () => {
        const startedAt = get().version;
        const template = await customEmailTemplateService.get();
        // A save, delete or company change while the request was in flight makes its answer stale.
        if (get().version === startedAt) set({ template, loaded: true });
        return template;
    },

    ensureLoaded: async () => (get().loaded ? get().template : get().fetch()),

    save: async (setting) => {
        await customEmailTemplateService.set(setting);
        set(state => ({ template: setting, loaded: true, version: state.version + 1 }));
    },

    remove: async () => {
        await customEmailTemplateService.remove();
        set(state => ({ template: { fr: null, en: null }, loaded: true, version: state.version + 1 }));
    },

    reset: () => set(state => ({ template: null, loaded: false, version: state.version + 1 })),
}));

export const selectHasCustomTemplate = (state: CustomEmailTemplateState): boolean =>
    definedLanguages(state.template).length > 0;
